import { useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '../../api/client'
import type { components } from '../../api/generated/schema'
import type { Envelope, Notice, NoticeLevel } from '../../lib/envelope'
import { patchSheets } from '../../lib/sheetCache'
import { caseKeys } from './queries'
import type { CaseSections, CaseSheet } from './types'

type Schema = components['schemas']

export type CaseEnvelope = Envelope<CaseSections>

const NOTICE_LEVELS: NoticeLevel[] = ['info', 'warn', 'error']

function toNotice(raw: Schema['Notice']): Notice {
  const level = raw.level as NoticeLevel | undefined
  return {
    level: level !== undefined && NOTICE_LEVELS.includes(level) ? level : 'info',
    code: raw.code ?? 'unknown',
    message: raw.message ?? '',
  }
}

export function toEnvelope(dto: Schema['CaseEnvelope']): CaseEnvelope {
  return {
    version: dto.version ?? 0,
    changed: (dto.changed ?? {}) as Partial<CaseSections>,
    notices: (dto.notices ?? []).map(toNotice),
  }
}

export interface CaseMutationOptions<TVariables> {
  caseId: number
  request: (variables: TVariables) => Promise<Schema['CaseEnvelope']>
  onEnvelope?: (envelope: CaseEnvelope) => void
  onFailure?: (error: unknown) => void
}

/**
 * A case has one cache entry, unlike a campaign — its three sections all arrive from the
 * one read. `patchSheets` still does the writing, so the filtering rule is stated once
 * for the whole client rather than re-derived per domain.
 *
 * Nothing here is optimistic: every write moves a server-computed count on `core`
 * (`sceneCount`, `majorCharacterCount`) alongside the list it changed.
 */
export function useCaseMutation<TVariables>(options: CaseMutationOptions<TVariables>) {
  const queryClient = useQueryClient()

  return useMutation<CaseEnvelope, unknown, TVariables>({
    mutationFn: async (variables) => toEnvelope(await options.request(variables)),

    onError: (error) => {
      options.onFailure?.(error)
    },

    onSuccess: (envelope) => {
      patchSheets<CaseSections, CaseSheet>(queryClient, [caseKeys.sheet(options.caseId)], envelope)
      options.onEnvelope?.(envelope)
    },
  })
}

type Envelopes = Schema['CaseEnvelope']

export const caseApi = {
  update: (caseId: number, body: Schema['CaseRequest']) =>
    api.put<Envelopes>(`/cases/${caseId}`, body),

  addCharacter: (caseId: number, characterId: number) =>
    api.post<Envelopes>(`/cases/${caseId}/characters`, { characterId }),

  // Addressed by the *character* id, not the join row's — the case's own id for the link
  // is in the DTO, but this route resolves the character within the case.
  removeCharacter: (caseId: number, characterId: number) =>
    api.delete<Envelopes>(`/cases/${caseId}/characters/${characterId}`),

  // Creating a scene is deliberately NOT here: it answers `201` with the new scene's own
  // `SceneSheetDto`, not a case envelope, because what a client does next is navigate to
  // it. It therefore cannot be patched into the case cache and lives in `useCreateScene`
  // (queries.ts) alongside the other create-and-invalidate mutations.

  deleteScene: (caseId: number, sceneId: number) =>
    api.delete<Envelopes>(`/cases/${caseId}/scenes/${sceneId}`),

  /**
   * Renumbers the case's scenes.
   *
   * The list must name **every** scene exactly once. The API rejects a partial ordering
   * with a 400 rather than half-applying it — a partial list would leave the unnamed
   * scenes on stale positions — so callers send the whole thing or nothing.
   */
  reorderScenes: (caseId: number, ids: number[]) =>
    api.put<Envelopes>(`/cases/${caseId}/scenes/order`, { ids }),
}
